import React, { useState, useCallback, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import Icon from '../icon'
import CharityPickerModal from './CharityPickerModal'
import Loading from '../loading'
import { useTranslation } from 'react-i18next'

export default function DefaultCharity() {
  const { t } = useTranslation('translation', { keyPrefix: 'users.edit.set_default_charity' })
  const { t: tPicker } = useTranslation()
  const { currentUser, setCurrentUser, fetchUser } = useCurrentUser()
  const { updateUser, getCurrentUser } = useApi()

  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedCharityId, setSelectedCharityId] = useState(
    currentUser?.favorite_charity_id
  )
  const [selectedCharityName, setSelectedCharityName] = useState(
    currentUser?.favorite_charity?.organization_name
  )

  // Sync local state from currentUser when it changes
  useEffect(() => {
    setSelectedCharityId(currentUser?.favorite_charity_id ?? null)
    setSelectedCharityName(currentUser?.favorite_charity?.organization_name ?? null)
  }, [currentUser?.favorite_charity_id, currentUser?.favorite_charity])

  const handleSelectCharity = useCallback(
    async charity => {
      if (isLoading) return

      const charityId = charity?.id || charity?.organization_id
      const charityName = charity?.organization_name
      
      const isCurrentlySelected = currentUser?.favorite_charity_id === charityId
      if (isCurrentlySelected) {
        setShowModal(false)
        return
      }
      
      // Select the new charity
      const newCharityId = charityId
      const newCharityName = charityName

      setSelectedCharityId(newCharityId)
      setSelectedCharityName(newCharityName)
      setIsLoading(true)

      try {
        const updateData = {
          favorite_charity_id: newCharityId,
        }

        const response = await updateUser(currentUser.id, updateData)


        if (response.status === 200) {
          // Update currentUser with the response data
          // The API returns favorite_charity_id and favorite_charity object with organization_name
          const updatedUser = response.data || {
            ...currentUser,
            favorite_charity_id: newCharityId,
            favorite_charity: {
              organization_name: newCharityName,
            },
          }
          setCurrentUser(updatedUser)
        } else {
          console.error('[DefaultCharity] Response not 200:', response)
          setSelectedCharityId(currentUser?.favorite_charity_id)
          setSelectedCharityName(currentUser?.favorite_charity?.organization_name)
        }
      } catch (error) {
        console.error('[DefaultCharity] Error updating user:', error)
        setSelectedCharityId(currentUser?.favorite_charity_id)
        setSelectedCharityName(currentUser?.favorite_charity?.organization_name)
      } finally {
        setIsLoading(false)
      }
    },
    [currentUser, isLoading, setCurrentUser, updateUser],
  )

  const handleRemoveCharity = useCallback(async () => {
    if (isLoading || !selectedCharityId) return

    setIsLoading(true)
    try {
      
      const response = await updateUser(currentUser.id, {
        favorite_charity_id: null,
      })

      if (response.status === 200) {
        const updatedUser = response.data || {
          ...currentUser,
          favorite_charity_id: null,
          favorite_charity: null,
        }
        setCurrentUser(updatedUser)
        setSelectedCharityId(null)
        setSelectedCharityName(null)
      } else {
        console.error('[DefaultCharity] handleRemoveCharity - Response not 200:', response)
      }
    } catch (error) {
      console.error('[DefaultCharity] handleRemoveCharity - Error:', error)
    } finally {
      setIsLoading(false)
    }
  }, [currentUser, isLoading, selectedCharityId, setCurrentUser, updateUser])

  return (
    <div className="container">
      <Loading show={isLoading} />
      <div className="row form-row mb-4">
        <h1>
          <Icon name="hands-holding-heart" className="business-title-icon" />
          Charity
        </h1>
        <h6 className="block-description">{t('charity_component_description')}</h6>
        <div className="row">
          <div className="col">
          {selectedCharityId && selectedCharityName ? (
            <div className="d-flex align-items-center mb-3" style={{ gap: '1rem' }}>
              <div>
                <Icon name="circle-star" className="me-2" style={{ color: '#FFC107' }} />
                <span className="text-capitalize">{selectedCharityName?.toLowerCase()}</span>
              </div>
              <Button
                variant="link"
                onClick={handleRemoveCharity}
                disabled={isLoading}
                className="p-0"
                style={{ textDecoration: 'none' }}
              >
                Remove
              </Button>
            </div>
          ) : (
            <p className="block-description mb-3">{t('message')}</p>
          )}
          <Button
            variant="primary"
            onClick={() => setShowModal(true)}
            disabled={isLoading}
          >
            <Icon name="search" className="me-2" />
            {selectedCharityId
              ? tPicker('charities.picker.change_favorite_charity_button')
              : tPicker('charities.picker.select_favorite_charity')}
          </Button>
          </div>
        </div>
      </div>
      <CharityPickerModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSelectCharity={handleSelectCharity}
        currentDefaultCharityId={selectedCharityId}
        favoriteCharityName={selectedCharityName}
        mode={selectedCharityId ? 'change' : 'select'}
        disabled={isLoading}
      />
    </div>
  )
}
